/**
 * SecretsSection — Settings tab "Secrets" sub-section (admin only).
 *
 * Lists the nine integration secrets stored in AWS SSM Parameter Store with a configured /
 * not-configured status badge. The secret VALUE is never displayed. Each row has a "Configure"
 * action that opens a small inline form to SET the value (POST /api/config/secrets/{key}).
 *
 * Non-admins never see this component (the parent Settings tab renders a contact-your-admin note
 * instead) and the backend re-enforces admin-only on both endpoints.
 */
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import {
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  KeyRound,
  RefreshCw,
} from 'lucide-react';

import {
  useSecretsStatus,
  useSetSecret,
  type SecretKey,
  type SecretsStatus,
} from '@/hooks/useSecrets';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

interface SecretMeta {
  key: SecretKey;
  label: string;
  ssmPath: string;
  usedFor: string;
}

/** Non-secret metadata rendered per row. Paths mirror packages/config/handlers/secrets-status.ts. */
const SECRETS: SecretMeta[] = [
  {
    key: 'github_token',
    label: 'GitHub Token',
    ssmPath: '/deliverpro/config/github-read-token',
    usedFor: 'Auto-create GitHub repos',
  },
  {
    key: 'slack_bot_token',
    label: 'Slack Bot Token',
    ssmPath: '/deliverpro/slack/bot-token',
    usedFor: 'Create/post to Slack channels',
  },
  {
    key: 'jira_api_token',
    label: 'Jira API Token',
    ssmPath: '/deliverpro/config/jira-api-token',
    usedFor: 'Create Jira projects',
  },
  {
    key: 'jira_base_url',
    label: 'Jira API Base URL',
    ssmPath: '/deliverpro/config/jira-base-url',
    usedFor: 'Jira REST calls (api.atlassian.com gateway)',
  },
  {
    key: 'jira_web_base_url',
    label: 'Jira Web Base URL',
    ssmPath: '/deliverpro/config/jira-web-base-url',
    usedFor: 'Browsable project links (https://<site>.atlassian.net)',
  },
  {
    key: 'jira_email',
    label: 'Jira Email',
    ssmPath: '/deliverpro/config/jira-email',
    usedFor: 'Jira auth email',
  },
  {
    key: 'clockify_api_token',
    label: 'Clockify API Token',
    ssmPath: '/deliverpro/config/clockify-api-token',
    usedFor: 'Sync resources + create projects',
  },
  {
    key: 'clockify_workspace_id',
    label: 'Clockify Workspace ID',
    ssmPath: '/deliverpro/config/clockify-workspace-id',
    usedFor: 'Clockify workspace',
  },
  {
    key: 'avoma_api_key',
    label: 'Avoma API Key',
    ssmPath: '/deliverpro/config/avoma-api-key',
    usedFor: 'Fetch meeting transcripts',
  },
];

function StatusBadge({ configured }: { configured: boolean | undefined }): JSX.Element {
  if (configured) {
    return (
      <Badge variant="success" className="gap-1">
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
        Configured
      </Badge>
    );
  }
  return (
    <Badge variant="warning" className="gap-1">
      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      Not configured
    </Badge>
  );
}

function SecretRow({
  meta,
  status,
}: {
  meta: SecretMeta;
  status: SecretsStatus | undefined;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const setSecret = useSetSecret();

  const configured = status?.[meta.key];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    try {
      await setSecret.mutateAsync({ key: meta.key, value: trimmed });
      toast.success(`${meta.label} saved`);
      setEditing(false);
      setValue('');
    } catch {
      // The global axios interceptor surfaces an error toast.
    }
  };

  return (
    <li className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-text-secondary" aria-hidden="true" />
          <div className="min-w-0">
            <p className="font-medium text-text-primary">{meta.label}</p>
            <p className="truncate font-mono text-xs text-text-secondary">{meta.ssmPath}</p>
            <p className="text-xs text-text-secondary">{meta.usedFor}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusBadge configured={configured} />
          {!editing && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              {configured ? 'Update' : 'Configure'}
            </Button>
          )}
        </div>
      </div>

      {editing && (
        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="flex flex-col gap-2 rounded-md border border-outlines bg-subtle-bg/30 p-3 sm:flex-row sm:items-end"
        >
          <div className="flex-1 space-y-1.5">
            <Label htmlFor={`secret-${meta.key}`} className="text-xs">
              New value for {meta.label}
            </Label>
            <Input
              id={`secret-${meta.key}`}
              type="password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Paste the secret value…"
              autoComplete="off"
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(false);
                setValue('');
              }}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={setSecret.isPending || !value.trim()}>
              {setSecret.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              Save
            </Button>
          </div>
        </form>
      )}
    </li>
  );
}

/** Admin-only secrets manager. Rendered only when the current user is an admin. */
export function SecretsSection(): JSX.Element {
  const { data, isLoading, isError, refetch, isFetching } = useSecretsStatus(true);

  return (
    <Card>
      <CardHeader>
        <h3 className="flex items-center justify-between gap-2 text-base font-semibold text-text-primary">
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
            Secrets
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void refetch()}
            disabled={isFetching}
            aria-label="Refresh secret status"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
          </Button>
        </h3>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm text-text-secondary">
          These secrets are stored in AWS SSM Parameter Store and are used by the system to
          auto-create and sync project resources. Values are write-only — they are never displayed
          here.
        </div>

        {isError && (
          <div className="flex flex-col items-start gap-2 rounded-md border border-error/40 bg-error/5 p-3 text-sm text-error">
            <span>Could not load secret status.</span>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3" role="status" aria-label="Loading secret status">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : (
          <ul className="divide-y divide-outlines">
            {SECRETS.map((meta) => (
              <SecretRow key={meta.key} meta={meta} status={data} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default SecretsSection;
